import { useCallback, useMemo, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    FormControl,
    FormControlLabel,
    IconButton,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';

import { ChangelogModal } from '../../changelog/ChangeLogModal';
import { Icon } from '../../shared/Icon';
import { publicationDtoToPortalPublication } from '../PortalPublication';
import { FileInput } from '../templates/inputs/FileInput';
import { AutomaticCategoryInput } from './AutomaticCategoryInput';
import { CategoryLocalizationKeyModal } from './CategoryLocalizationKeyModal';
import { CategoryTypeInput } from './CategoryTypeInput';
import { DeleteCategoryModal } from './DeleteCategoryModal';
import { PortalCategoryType, UnityColor } from '@lifeserver/shared';

import {
    PortalCategoryDTO,
    PortalSettingsDTO,
    usePortalCategories,
    usePortalLocalization,
    usePortalSchedule,
    usePortalSettings,
    useSavePortalLocalization,
} from '../../hooks/portal';
import { useRemoteConfig } from '../../hooks/remoteConfig';
import { useTranslations } from '../../hooks/translations';

import { getPublicationStatus } from '../portalUtils';
import { sheetTranslationsToLocalizations } from '../translationUtils';
import styles from './Category.module.scss';

import { MediaProcessingReporterContext } from '../templates/inputs/mediaProcessingReporter';

const ACTIVE_COLOR: UnityColor = { r: 0.012, g: 0.839, b: 0.812, a: 1 };
const INACTVE_COLOR: UnityColor = { r: 1, g: 1, b: 1, a: 1 };

interface CategoryModalProps {
    open: boolean;
    initialValue?: PortalCategoryDTO;
    onClose: () => void;
    onSave: (category: PortalCategoryDTO) => void;
}

export const CategoryModal = ({
    open,
    onClose,
    onSave,
    initialValue,
}: CategoryModalProps) => {
    const [changelogOpen, setChangelogOpen] = useState(false);
    const { data: categorySchedule } = usePortalSchedule(initialValue?.id);
    const { data: allSettings } = usePortalSettings();
    const publications = (categorySchedule?.publications ?? []).map(
        publicationDtoToPortalPublication,
    );

    const { configKeys: remoteConfigValues } = useRemoteConfig();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [localizationKeyModalOpen, setLocalizationKeyModalOpen] =
        useState(false);
    const [value, setValue] = useState<PortalCategoryDTO>(
        () =>
            initialValue ?? {
                type: 'manual',
                name: 'Category Name',
                id: crypto.randomUUID(),
            },
    );

    const { data: allLocalizations = [] } = usePortalLocalization();
    // The Google Sheet (`CMS` range) is the same source posts import their
    // translations from. Pulling it here lets a category adopt a shared key
    // that only lives in the sheet and fan its languages into the portal.
    const { data: sheetTranslations } = useTranslations('CMS', '');
    const defaultLocalizationId = `category.${value.id}.name`;
    const localizationId = value.portalLocalizationId ?? defaultLocalizationId;

    const isLinkedToExistingKey = useMemo(
        () =>
            localizationId !== defaultLocalizationId &&
            (allLocalizations.some((loc) => loc.id === localizationId) ||
                Boolean(sheetTranslations?.[localizationId])),
        [
            allLocalizations,
            defaultLocalizationId,
            localizationId,
            sheetTranslations,
        ],
    );

    const loadedEnglishTranslation = useMemo(() => {
        const dbRow = allLocalizations.find(
            (loc) => loc.id === localizationId && loc.language === 'en',
        );
        if (dbRow?.translation) {
            return dbRow.translation;
        }
        // Fall back to the sheet's English value so a category linked to a
        // sheet-only key still shows a preview of what players will see.
        return sheetTranslations?.[localizationId]?.en ?? '';
    }, [allLocalizations, localizationId, sheetTranslations]);

    const [englishTranslationEdit, setEnglishTranslationEdit] = useState<
        string | null
    >(null);
    const englishTranslation =
        englishTranslationEdit ?? loadedEnglishTranslation;

    const [keyCopied, setKeyCopied] = useState(false);
    const handleCopyLocalizationKey = async () => {
        try {
            await navigator.clipboard.writeText(localizationId);
            setKeyCopied(true);
            setTimeout(() => setKeyCopied(false), 1500);
        } catch {
            // Ignore clipboard failures (e.g. permission denied or
            // unsupported environment) — surfacing a UI error here would
            // be noisier than the convenience is worth.
        }
    };

    const saveLocalization = useSavePortalLocalization();

    const variantRequired = !!value.abTestCase;
    const variantError = variantRequired && !value.variant;

    // A category must always have a thumbnail image. Treat undefined, null
    // and whitespace-only alike so a cleared upload also counts as missing.
    const imageMissing = !value.image?.trim();
    // FileInput assigns a temporary blob: URL the moment a file is picked and
    // only swaps in the final URL once the upload finishes. Treat that interim
    // state as "not ready to save" so we never persist a blob: URL — the same
    // guard the events calendar variant validation uses.
    const imageUploading = !!value.image?.startsWith('blob:');
    const [imageProcessing, setImageProcessing] = useState(false);
    const reportImageProcessing = useCallback(
        (_key: string, isProcessing: boolean) =>
            setImageProcessing(isProcessing),
        [],
    );

    const { deleteCategory } = usePortalCategories();

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const activeToday = publications.filter((publication) => {
        const status = getPublicationStatus(publication);
        return status === 'published';
    });

    const futurePublications = publications.filter(
        (publication) => getPublicationStatus(publication) === 'scheduled',
    );
    const uniqueFutureDays = Array.from(
        new Set(
            futurePublications
                .map((pub) =>
                    pub.startTime
                        ? new Date(pub.startTime).toLocaleDateString()
                        : null,
                )
                .filter((d): d is string => Boolean(d)),
        ),
    );
    const handleSave = async () => {
        let nextValue = value;

        if (value.type === 'eventsCalendar') {
            const sheetForKey = sheetTranslations?.[localizationId];

            if (sheetForKey) {
                // The chosen key lives in the Google Sheet — import every
                // language into `portal_localization` so the feed serves the
                // category name localized, exactly like posts adopt their
                // sheet translations. This also covers English.
                const localizations = sheetTranslationsToLocalizations(
                    [localizationId],
                    sheetTranslations ?? {},
                    { skipEmpty: true },
                );
                if (localizations.length > 0) {
                    await saveLocalization.mutateAsync({ localizations });
                }
            } else if (!isLinkedToExistingKey) {
                // Brand-new key: persist the English title the editor typed so
                // it shows up under Portal Translations. We skip empty input so
                // clearing the field on purpose doesn't wipe a translation row.
                const trimmed = englishTranslation.trim();
                if (trimmed.length > 0) {
                    await saveLocalization.mutateAsync({
                        localizations: [
                            {
                                id: localizationId,
                                language: 'en',
                                translation: trimmed,
                            },
                        ],
                    });
                }
            }
            // Otherwise the key already exists in the database but not the
            // sheet. We can't tell from here whether another post or category
            // also uses it, so to be safe we leave it untouched rather than
            // risk overwriting a translation that might be shared elsewhere.

            nextValue = { ...value, portalLocalizationId: localizationId };
        }

        onSave(nextValue);
        onClose();
    };

    const handleLocalizationKeyChange = (nextKey: string) => {
        const normalized = nextKey.trim();
        const newKey =
            normalized && normalized !== defaultLocalizationId
                ? normalized
                : undefined;
        setValue({ ...value, portalLocalizationId: newKey });
        setEnglishTranslationEdit(null);
    };

    const handleTypeChange = (categoryType: PortalCategoryType) => {
        if (categoryType !== 'manual') {
            setValue({
                ...value,
                type: categoryType,
                startDate: null,
                endDate: null,
                schedule: null,
            });
        } else {
            setValue({
                ...value,
                type: categoryType,
            });
        }
    };

    const handleConfirmDelete = async () => {
        await deleteCategory.mutateAsync({ categoryId: value.id });
        setDeleteDialogOpen(false);
        onClose();
    };

    const handleCancelDelete = () => {
        setDeleteDialogOpen(false);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogContent
                className={styles.dialogContent}
                dividers={false}
                sx={{ overflow: 'visible' }}
            >
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <fieldset className={styles.fieldset}>
                            <legend className={styles.legend}>Info</legend>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                }}
                            >
                                <TextField
                                    label="Category Name"
                                    placeholder="Fill in category name here"
                                    fullWidth
                                    margin="none"
                                    value={value.name}
                                    onChange={(e) =>
                                        setValue({
                                            ...value,
                                            name: e.target.value,
                                        })
                                    }
                                />
                                <Button
                                    aria-label="Delete"
                                    color="error"
                                    variant="outlined"
                                    size="large"
                                    onClick={() => setDeleteDialogOpen(true)}
                                    disabled={
                                        !value.id ||
                                        value.name === 'Main Category'
                                    }
                                    endIcon={<Icon name="delete" />}
                                    sx={{ height: 56 }}
                                >
                                    Delete
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="large"
                                    onClick={() => setChangelogOpen(true)}
                                    sx={{ height: 56 }}
                                >
                                    Show Changelog
                                </Button>
                            </Box>
                            <Box
                                className={`${styles.categoryThumbnailLabel} ${value.image ? styles.categoryThumbnailLabelHasImage : ''}`}
                            >
                                <Typography
                                    variant="body2"
                                    gutterBottom
                                    color={imageMissing ? 'error' : 'inherit'}
                                >
                                    Category Thumbnail *
                                </Typography>

                                <MediaProcessingReporterContext.Provider
                                    value={reportImageProcessing}
                                >
                                    <FileInput
                                        allowedFileTypes="image"
                                        value={
                                            value.image
                                                ? {
                                                      url: value.image,
                                                      fileType: 'image',
                                                  }
                                                : undefined
                                        }
                                        onChange={(media) =>
                                            setValue({
                                                ...value,
                                                image: media?.url,
                                            })
                                        }
                                    />
                                </MediaProcessingReporterContext.Provider>
                                {imageMissing && (
                                    <Typography
                                        variant="caption"
                                        color="error"
                                        sx={{ display: 'block', mt: 0.5 }}
                                    >
                                        Required — a category must have a
                                        thumbnail image.
                                    </Typography>
                                )}
                                {(imageUploading || imageProcessing) && (
                                    <Typography
                                        variant="caption"
                                        color="warning.main"
                                        sx={{ display: 'block', mt: 0.5 }}
                                    >
                                        Image is still{' '}
                                        {imageUploading
                                            ? 'uploading'
                                            : 'processing'}{' '}
                                        — please wait for it to finish before
                                        saving.
                                    </Typography>
                                )}
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={!!value.tint}
                                            onChange={(e) =>
                                                setValue({
                                                    ...value,
                                                    tint: e.target.checked,
                                                    activeColor: e.target
                                                        .checked
                                                        ? ACTIVE_COLOR
                                                        : null,
                                                    inactiveColor: e.target
                                                        .checked
                                                        ? INACTVE_COLOR
                                                        : null,
                                                })
                                            }
                                        />
                                    }
                                    label="Tinted"
                                />
                            </Box>
                            <Accordion sx={{ mt: 2 }}>
                                <AccordionSummary
                                    expandIcon={<Icon name="expand_more" />}
                                    aria-controls="category-info-content"
                                    id="category-info-header"
                                >
                                    <Typography
                                        variant="subtitle1"
                                        sx={{ fontWeight: 'bold' }}
                                    >
                                        A/B Test(Optional)
                                    </Typography>
                                </AccordionSummary>

                                <AccordionDetails>
                                    <Box
                                        className={styles.categoryInfoBody}
                                        sx={{ gap: 4 }}
                                    >
                                        <Box sx={{ display: 'grid', gap: 2 }}>
                                            <Autocomplete
                                                options={
                                                    remoteConfigValues ?? []
                                                }
                                                getOptionLabel={(option) =>
                                                    option
                                                }
                                                value={value.abTestCase ?? ''}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Remote Config Key"
                                                        placeholder="Select a config key"
                                                        fullWidth
                                                        margin="normal"
                                                    />
                                                )}
                                                onChange={(_, newValue) => {
                                                    setValue({
                                                        ...value,
                                                        abTestCase:
                                                            newValue || '',
                                                        variant: newValue
                                                            ? value.variant
                                                            : '',
                                                    });
                                                }}
                                            />

                                            <FormControl
                                                fullWidth
                                                error={variantError}
                                                required={variantRequired}
                                            >
                                                <TextField
                                                    label="Value"
                                                    value={value.variant ?? ''}
                                                    onChange={(e) =>
                                                        setValue({
                                                            ...value,
                                                            variant:
                                                                e.target.value,
                                                        })
                                                    }
                                                ></TextField>
                                            </FormControl>
                                        </Box>
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                            <Box sx={{ mt: 2 }}>
                                <Autocomplete<PortalSettingsDTO | null>
                                    options={allSettings ?? []}
                                    getOptionLabel={(option) =>
                                        option?.name ?? ''
                                    }
                                    value={
                                        (allSettings ?? []).find(
                                            (s) => s.id === value.settingsId,
                                        ) ?? null
                                    }
                                    isOptionEqualToValue={(option, val) =>
                                        option?.id === val?.id
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Portal Settings Override"
                                            placeholder="Use Active Setting (default)"
                                            fullWidth
                                            margin="normal"
                                        />
                                    )}
                                    onChange={(_, newValue) => {
                                        setValue({
                                            ...value,
                                            settingsId: newValue?.id ?? null,
                                        });
                                    }}
                                />
                            </Box>
                        </fieldset>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <fieldset className={styles.fieldset}>
                            <legend className={styles.legend}>Type</legend>
                            <CategoryTypeInput
                                value={value.type}
                                onChange={handleTypeChange}
                            />

                            {value.type === 'eventsCalendar' && (
                                <Box
                                    sx={{
                                        mt: 2,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1,
                                    }}
                                >
                                    <TextField
                                        label="English title (shown to players)"
                                        placeholder="e.g. Summer Events"
                                        fullWidth
                                        size="small"
                                        value={englishTranslation}
                                        onChange={(e) =>
                                            setEnglishTranslationEdit(
                                                e.target.value,
                                            )
                                        }
                                        disabled={isLinkedToExistingKey}
                                        required={!isLinkedToExistingKey}
                                        error={
                                            !isLinkedToExistingKey &&
                                            englishTranslation.trim() === ''
                                        }
                                        helperText={
                                            isLinkedToExistingKey
                                                ? 'This category reuses an existing translation key — its translations are saved with the category. Edit values from the Portal Translations page.'
                                                : englishTranslation.trim() ===
                                                    ''
                                                  ? 'Required — this is what players see for the category.'
                                                  : 'Saved under the translation key below. Other languages are managed from the Portal Translations page.'
                                        }
                                    />

                                    {/* Compact two-row key display. Long
                                        UUID-based keys would otherwise wrap
                                        awkwardly mid-string on the same row
                                        as the label and action buttons. */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 0.5,
                                            color: 'text.secondary',
                                            fontSize: 13,
                                        }}
                                    >
                                        {/* Row 1: label, status tags, Change button */}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                flexWrap: 'wrap',
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                sx={{ flexShrink: 0 }}
                                            >
                                                Translation key:
                                            </Typography>
                                            {localizationId ===
                                                defaultLocalizationId && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{ fontStyle: 'italic' }}
                                                >
                                                    (auto-generated)
                                                </Typography>
                                            )}
                                            {isLinkedToExistingKey && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: 'warning.main',
                                                        fontStyle: 'italic',
                                                    }}
                                                >
                                                    (shared key)
                                                </Typography>
                                            )}
                                            <Box sx={{ flex: 1 }} />
                                            <Button
                                                size="small"
                                                variant="text"
                                                onClick={() =>
                                                    setLocalizationKeyModalOpen(
                                                        true,
                                                    )
                                                }
                                            >
                                                Change…
                                            </Button>
                                        </Box>
                                        {/* Row 2: monospace key + copy button */}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                            }}
                                        >
                                            <Box
                                                component="span"
                                                sx={{
                                                    fontFamily: 'monospace',
                                                    wordBreak: 'break-all',
                                                    color: 'text.primary',
                                                    flex: 1,
                                                }}
                                            >
                                                {localizationId}
                                            </Box>
                                            <Tooltip
                                                title={
                                                    keyCopied
                                                        ? 'Copied!'
                                                        : 'Copy translation key'
                                                }
                                            >
                                                <IconButton
                                                    size="small"
                                                    onClick={
                                                        handleCopyLocalizationKey
                                                    }
                                                    aria-label="Copy translation key"
                                                >
                                                    <Icon
                                                        name={
                                                            keyCopied
                                                                ? 'check'
                                                                : 'content_copy'
                                                        }
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>

                                    <CategoryLocalizationKeyModal
                                        open={localizationKeyModalOpen}
                                        value={localizationId}
                                        defaultKey={defaultLocalizationId}
                                        allLocalizations={allLocalizations}
                                        sheetTranslations={sheetTranslations}
                                        onClose={() =>
                                            setLocalizationKeyModalOpen(false)
                                        }
                                        onApply={handleLocalizationKeyChange}
                                    />
                                </Box>
                            )}

                            {value.type === 'automatic' && (
                                <AutomaticCategoryInput
                                    rules={value.schedule ?? []}
                                    endDate={
                                        value.endDate
                                            ? new Date(value.endDate)
                                            : undefined
                                    }
                                    startDate={
                                        value.startDate
                                            ? new Date(value.startDate)
                                            : undefined
                                    }
                                    onEndDateChange={(date) => {
                                        setValue({
                                            ...value,
                                            endDate: date.toISOString(),
                                        });
                                    }}
                                    onStartDateChange={(date) => {
                                        setValue({
                                            ...value,
                                            startDate: date.toISOString(),
                                        });
                                    }}
                                    onRuleAdded={(rule) => {
                                        setValue({
                                            ...value,
                                            schedule: [
                                                ...(value.schedule ?? []),
                                                rule,
                                            ],
                                        });
                                    }}
                                    onRuleChanged={(oldRule, newRule) => {
                                        setValue({
                                            ...value,
                                            schedule: (
                                                value.schedule ?? []
                                            ).map((rule) =>
                                                rule === oldRule
                                                    ? newRule
                                                    : rule,
                                            ),
                                        });
                                    }}
                                    onRuleRemoved={(ruleToRemove) => {
                                        setValue({
                                            ...value,
                                            schedule: (
                                                value.schedule ?? []
                                            ).filter(
                                                (rule) => rule !== ruleToRemove,
                                            ),
                                        });
                                    }}
                                />
                            )}
                        </fieldset>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions>
                <Button
                    variant="contained"
                    color="primary"
                    className={styles.dialogActionsButton}
                    onClick={handleSave}
                    disabled={
                        value.name.trim() === '' ||
                        // A category must always have a thumbnail image, and it
                        // must be fully uploaded and processed.
                        imageMissing ||
                        imageUploading ||
                        imageProcessing ||
                        (!!value.abTestCase && !value.variant) ||
                        (value.type === 'automatic' &&
                            (!value.schedule || value.schedule.length === 0)) ||
                        // For eventsCalendar the English title is what
                        // players see, so an empty one would leave the
                        // category nameless in-game.
                        (value.type === 'eventsCalendar' &&
                            !isLinkedToExistingKey &&
                            englishTranslation.trim() === '')
                    }
                >
                    {value ? 'Save Changes' : 'Create'}
                </Button>
            </DialogActions>
            <DeleteCategoryModal
                open={deleteDialogOpen}
                onCancel={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                categoryName={value?.name}
                activeToday={activeToday}
                futurePublications={futurePublications}
                uniqueFutureDays={uniqueFutureDays}
            />
            <ChangelogModal
                open={changelogOpen}
                onClose={() => setChangelogOpen(false)}
                entity="category"
                entityId={value.id}
            />
        </Dialog>
    );
};
